import { Grid, Card, Box, CardContent, Stack, Typography, ButtonBase, Button, Paper, IconButton, Tooltip } from '@mui/material';
import { useContext, useState } from 'react';
import { ExerciseDTO, PlanWorkout } from '@/generated';
import { ImageFromServer } from '@/components/images/ImageFromServer';
import { Style } from '@mui/icons-material';
import { AppContext } from '@/contexts/app.context';
import { AddExercisesDrawer } from './AddExerciseDrawer';
import { useNavigate, useParams } from 'react-router-dom';
import { DefaultContainer } from '@/components/DefaultContainer';
import { EditFab } from '@/components/EditFab';
import { UpdateWorkoutModal } from '../UpdateWorkoutModal';
import { isPlanEditor } from '../utils';
import { PlanExercise } from '@/generated/models/PlanExercise';
import { useDeckQuery, usePlanQuery, useBodyZoneListQuery, useAllExercisesQuery, useDeckExercisesQuery } from '@/hooks/query-hooks';
import { pathMap } from '@/navigation/paths';
import { PlanPermissionsElement } from '../PlanPermissionsIcon';
import { NavigateToDeckExerciseButton, NavigateToDeckExerciseInExtraWindowButton } from '@/components/icon-buttons/NavigateToDeckExerciseButton';
import { useUpdatePlan } from '@/hooks/plan-mutation.hooks';
import { updateWorkoutExerciseMetrics } from '../creationUtils';
import { CurrentPlanStage } from '@/components/CurrentPlanStage';
import { ExerciseMetrics } from '@/screens/exercises/list-elements/ExerciseMetrics';

export const Workout = () => {
  const { workoutUnitId, workoutId, planId } = useParams();
  const context = useContext(AppContext);
  const { plan } = usePlanQuery(planId);
  const unit = plan?.units.find((g) => g.idClient === workoutUnitId);
  const workout = plan?.units.find((g) => g.idClient === workoutUnitId)?.workouts.find((w) => w.idClient === workoutId);
  const { deck } = useDeckQuery(workout?.deckId);
  const { exercises } = useAllExercisesQuery(true);
  const { exercises: deckExercises } = useDeckExercisesQuery(deck?.id);
  context.setExtraTitle(`: ${plan?.title} - ${unit?.name} - ${workout?.name}`);
  const navigate = useNavigate();
  const localUser = context.state?.userProfile;

  const updatePlan = useUpdatePlan();

  //Drawer stuff
  const [roundIndex, setRoundIndex] = useState<number | undefined>(undefined);
  const [exerciseIndex, setExerciseIndex] = useState<number | undefined>(undefined);
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [selectedExercise, setSelectedExercise] = useState<ExerciseDTO>();
  const [showWorkoutUpdateModal, setShowWorkoutUpdateModal] = useState(false);

  return (
    <DefaultContainer>
      {plan && workout && workoutUnitId && (
        <UpdateWorkoutModal plan={plan} workout={workout} workoutUnitId={workoutUnitId} setShow={setShowWorkoutUpdateModal} show={showWorkoutUpdateModal} />
      )}
      <Paper sx={{ my: 1, zIndex: 1, padding: 1, position: 'sticky', top: { xs: 60, sm: 65 } }}>
        <Grid sx={{ alignContent: 'center', alignItems: 'center' }} container gap={2} direction={'row'}>
          <Stack sx={{ alignItems: 'center' }} gap={2} direction={'row'}>
            <Button
              variant="outlined"
              onClick={() => {
                navigate(pathMap.Plans);
              }}
            >
              &lt;&nbsp;&nbsp;Plans
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                navigate(`${pathMap.Plan}/${planId}`);
              }}
            >
              &lt;&nbsp;&nbsp;{plan?.title}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                navigate(`${pathMap.Plan}/${planId}/${unit?.idClient}`);
              }}
            >
              &lt;&nbsp;&nbsp;{unit?.name}
            </Button>
            <CurrentPlanStage name={workout?.name ? workout?.name : 'Nameless Workout'} />
          </Stack>
          <PlanPermissionsElement plan={plan} />
        </Grid>
      </Paper>
      <Paper
        sx={{
          my: 1,
          zIndex: 1,
          padding: 1,
          position: 'sticky',
          top: { xs: 60, sm: 125 },
        }}
      >
        <Stack gap={1} direction={'row'} sx={{ alignContent: 'center', alignItems: 'center', textAlign: 'center', gap: 2 }}>
          <Stack direction={'row'} sx={{ alignContent: 'center', alignItems: 'center', textAlign: 'center' }}>
            <IconButton href={`${pathMap.Exercises}/${deck?.id}`}>
              <Style />
            </IconButton>
            <Box>{deck?.name}</Box>
          </Stack>
          {isPlanEditor(plan, localUser) && (
            <Tooltip title="Update all exercises in this plan with the current values (weight, reps, time) from the corresponding deck.">
              <Button
                variant="outlined"
                onClick={async () => {
                  if (workout && deckExercises && exercises) updateWorkoutExerciseMetrics(workout, deckExercises, exercises);
                  if (plan) await updatePlan.mutateAsync({ plan: plan, notifyUsers: false });
                }}
              >
                Update Metrics
              </Button>
            </Tooltip>
          )}
          <Box sx={{ flex: 1, textAlign: 'end', mr: 1 }}>Nr. of Exercises: {workout?.rounds.flatMap((r) => r.exercises).filter((ex) => ex !== null).length}</Box>
        </Stack>
      </Paper>

      {exercises && deck && (
        <AddExercisesDrawer
          plan={plan}
          workout={workout}
          unit={unit}
          deck={deck}
          selectedExercise={selectedExercise}
          show={showDrawer}
          setShow={setShowDrawer}
          roundIndex={roundIndex}
          exerciseIndex={exerciseIndex}
          exerciseList={deckExercises ?? []}
        />
      )}
      <Box sx={{ justifyItems: 'center' }}>
        {workout && (
          <WorkoutGrid
            workout={workout}
            onElementClicked={(ri, ei, ex) => {
              if (isPlanEditor(plan, localUser)) {
                setShowDrawer(true);
                setRoundIndex(ri);
                setExerciseIndex(ei);
                setSelectedExercise(ex);
              }
            }}
            exercises={exercises}
          />
        )}
      </Box>
      {isPlanEditor(plan, localUser) && (
        <EditFab
          setShow={(show) => {
            setShowWorkoutUpdateModal(show);
          }}
        />
      )}
    </DefaultContainer>
  );
};

const WorkoutGrid = (props: { workout: PlanWorkout; exercises?: ExerciseDTO[]; onElementClicked: (roundIndex: number, exerciseIndex: number, exercise?: ExerciseDTO) => void }) => {
  const { onElementClicked, workout } = props;
  const maxLen = workout?.rounds.map((r) => r.exercises.length).reduce((p, c) => (p > c ? p : c), 0) ?? 0;
  const mapped: Array<PlanExercise | null> = [];

  for (let i = 0; i < maxLen; i++) {
    workout.rounds.forEach((r) => {
      mapped.push(r.exercises[i]);
    });
  }

  return (
    <Box sx={{ justifyContent: 'center', display: 'grid', gridAutoRows: '1fr', mt: 1 }} gridTemplateColumns={`repeat(${workout?.rounds.length}, 1fr)`}>
      {mapped?.map((e, i) => {
        const roundIndex = i % (workout?.rounds.length ?? 0);
        const exerciseIndex = Math.floor(i / (workout?.rounds.length ?? 1));

        const current = props.exercises?.find((ex) => ex.id === e?.exerciseId);
        const modCurrent = current && e ? { ...current, config: e.exerciseConfig } : undefined;

        return (
          <>
            <CellElement
              deckId={workout.deckId}
              exercise={modCurrent}
              onClick={async () => {
                onElementClicked(roundIndex, exerciseIndex, modCurrent);
              }}
            />
          </>
        );
      })}
    </Box>
  );
};

const CellElement = (props: { onClick: () => void; exercise?: ExerciseDTO; deckId: string }) => {
  const { onClick, exercise, deckId } = props;

  return exercise ? <ExerciseCard deckId={deckId} exercise={exercise} timeout={1000} onClick={onClick} /> : <EmptyCard onClick={onClick} />;
};

interface EmptyCardProps {
  onClick: () => void;
}

interface CardExerciseProps extends EmptyCardProps {
  exercise: ExerciseDTO;
  deckId: string;
  timeout: number;
}

export const EmptyCard = (props: EmptyCardProps) => {
  return (
    <Card sx={{ margin: 0.5, alignContent: 'center', justifyContent: 'center', justifyItems: 'center', textAlign: 'center' }}>
      <ButtonBase onClick={props.onClick}>
        <CardContent sx={{}}>&lt; Empty Slot &gt;</CardContent>
      </ButtonBase>
    </Card>
  );
};

export const ExerciseCard = (props: CardExerciseProps) => {
  const { bodyZoneList } = useBodyZoneListQuery();
  const { exercise, deckId } = props;

  return (
    <Card onClick={props.onClick} sx={{ margin: 0.5, maxWidth: 300 }}>
      <CardContent sx={{}}>
        <Grid container>
          <Grid size={5}>
            <ImageFromServer sx={{ maxWidth: '100%', aspectRatio: 1, ml: 0.75, borderRadius: 0.75 }} thumbnail={true} alt="" id={exercise.imageId} />
          </Grid>
          <Grid size={7}>
            <Stack sx={{ textAlign: 'left', ml: 2.5, flexDirection: 'column' }}>
              <Typography
                sx={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  WebkitLineClamp: 2, // limit to 3 lines
                }}
                variant="body1"
              >
                {exercise.name}
              </Typography>
              <Typography sx={{ mt: 1.5 }} variant="body2">
                {bodyZoneList?.find((bz) => bz.id === props.exercise.mainBodyZoneId)?.name ?? 'N.A.'}
              </Typography>
              <Stack direction={'row'}>
                <NavigateToDeckExerciseButton deckId={deckId} exerciseId={exercise.id} />
                <NavigateToDeckExerciseInExtraWindowButton deckId={deckId} exerciseId={exercise.id} />
              </Stack>
            </Stack>
          </Grid>
          <Box sx={{ mt: 1, px: 1 }}>
            <ExerciseMetrics exercise={exercise} />
          </Box>
        </Grid>
      </CardContent>
    </Card>
  );
};
